import {SortingState} from '@tanstack/table-core';
import {MRT_ColumnDef, MRT_Row, MRT_RowData} from 'material-react-table';
import React, {useMemo, useState} from 'react';
import {Box, Divider, Stack} from '@mui/material';
import {Body, Headline, Avatar, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {CampaignProps} from '@/types/campaign';
import {CampaignStatsCreator} from '@/types/campaignStatsCreator';
import {formatNumber} from '@/views/campaigns/stats/utils';
import SearchInput from '@/components/search-input';
import Table from '@/components/table';
import layoutStyles from '../campaign-stats-table-layout.module.scss';
import styles from './CreatorsStatsTable.module.scss';
import {
  API_FIELD_TO_COLUMN_ID,
  COLUMN_ID_TO_API_FIELD,
  COLUMN_IDS,
  COLUMN_SIZES,
  CreatorsStatsColumnId,
  getDefaultCreatorsSort,
} from './constants';

function createCellRenderer<T extends MRT_RowData>(
  render: (row: MRT_Row<T>) => React.ReactNode
): ({row}: {row: MRT_Row<T>}) => React.ReactNode {
  return ({row}) => render(row);
}

interface CreatorsStatsTableProps {
  testID?: string;
  creators: CampaignStatsCreator[];
  campaign?: CampaignProps;
  totalCount?: number;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onSort: (sort: string) => void;
  onSearch: (query: string) => void;
  searchQuery: string;
  sort?: string;
}

function parseSortString(sortStr: string): SortingState {
  if (!sortStr) {
    return [];
  }

  const descending = sortStr.startsWith('-');
  const field = descending ? sortStr.slice(1) : sortStr;
  const columnId = API_FIELD_TO_COLUMN_ID[field];

  if (!columnId || columnId === COLUMN_IDS.creator) {
    return [];
  }

  return [{id: columnId, desc: descending}];
}

function buildSortString(sorting: SortingState, defaultSort: string): string {
  const [sort] = sorting;
  if (!sort) {
    return defaultSort;
  }

  const apiField = COLUMN_ID_TO_API_FIELD[sort.id as CreatorsStatsColumnId];
  if (!apiField) {
    return defaultSort;
  }

  return sort.desc ? `-${apiField}` : apiField;
}

function CreatorCell({row}: {row: MRT_Row<CampaignStatsCreator>}) {
  const creator = row.original;

  return (
    <Box className={layoutStyles.creatorCell}>
      <Avatar
        size="sm"
        src={creator.imageUrl ?? undefined}
        type={creator.imageUrl ? 'picture' : 'initials'}
        initials={creator.displayName.charAt(0)}
      />
      <Label size="sm" className={styles.creatorName}>
        {creator.displayName}
      </Label>
    </Box>
  );
}

function CreatorsStatsTable({
  testID = 'creators-stats-table',
  creators,
  campaign,
  totalCount = creators.length,
  isLoading,
  isFetching,
  isFetchingNextPage = false,
  hasNextPage,
  onLoadMore,
  onSort,
  onSearch,
  searchQuery,
  sort,
}: CreatorsStatsTableProps) {
  const showClicks = (campaign?.campaignTrackingUrls?.length ?? 0) > 0;
  const defaultSort = getDefaultCreatorsSort(showClicks);

  const [sorting, setSorting] = useState<SortingState>(() =>
    parseSortString(sort ?? defaultSort)
  );

  const showImpressions = campaign?.impressionsVisible ?? false;

  const visibleColumnIds = useMemo(() => {
    const ids: CreatorsStatsColumnId[] = [COLUMN_IDS.creator];
    if (showClicks) {
      ids.push(COLUMN_IDS.clicks);
    }
    if (showImpressions) {
      ids.push(COLUMN_IDS.impressions);
    }
    ids.push(COLUMN_IDS.posts);
    return ids;
  }, [showClicks, showImpressions]);

  const handleSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const nextSorting =
      typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(nextSorting);
    onSort(buildSortString(nextSorting, defaultSort));
  };

  React.useEffect(() => {
    setSorting(parseSortString(sort ?? defaultSort));
  }, [defaultSort, sort]);

  // When the first page doesn't fill the scroll container, scroll-based
  // pagination never fires — keep loading until the table is scrollable.
  React.useEffect(() => {
    if (isLoading || isFetchingNextPage || !hasNextPage) {
      return;
    }

    const container = document
      .querySelector(`[data-testid="${testID}-table"]`)
      ?.closest('.MuiTableContainer-root');

    if (container && container.scrollHeight <= container.clientHeight) {
      onLoadMore();
    }
  }, [
    creators.length,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    onLoadMore,
    testID,
  ]);

  const columns: MRT_ColumnDef<CampaignStatsCreator>[] = useMemo(() => {
    const defs: MRT_ColumnDef<CampaignStatsCreator>[] = [
      {
        id: COLUMN_IDS.creator,
        accessorKey: 'displayName',
        header: translate('Creator'),
        Cell: createCellRenderer((row) => <CreatorCell row={row} />),
        enableSorting: false,
        size: COLUMN_SIZES[COLUMN_IDS.creator],
        grow: 0,
      },
    ];

    if (showClicks) {
      defs.push({
        id: COLUMN_IDS.clicks,
        accessorKey: 'clickCount',
        header: translate('Clicks'),
        Cell: createCellRenderer((row) =>
          formatNumber(row.original.clickCount)
        ),
        enableSorting: true,
        size: COLUMN_SIZES[COLUMN_IDS.clicks],
        grow: 1,
      });
    }

    if (showImpressions) {
      defs.push({
        id: COLUMN_IDS.impressions,
        accessorKey: 'impressionCount',
        header: translate('Impressions'),
        Cell: createCellRenderer((row) =>
          formatNumber(row.original.impressionCount)
        ),
        enableSorting: true,
        size: COLUMN_SIZES[COLUMN_IDS.impressions],
        grow: 1,
      });
    }

    defs.push({
      id: COLUMN_IDS.posts,
      accessorKey: 'postCount',
      header: translate('Posts'),
      Cell: createCellRenderer((row) => formatNumber(row.original.postCount)),
      enableSorting: true,
      size: COLUMN_SIZES[COLUMN_IDS.posts],
      grow: 1,
    });

    return defs.map((column) => ({
      ...column,
      muiTableHeadCellProps: {
        align: column.id === COLUMN_IDS.creator ? 'left' : 'right',
      },
      muiTableBodyCellProps: {
        align: column.id === COLUMN_IDS.creator ? 'left' : 'right',
      },
    }));
  }, [showClicks, showImpressions]);

  const handleEndReached = () => {
    if (hasNextPage && !isFetchingNextPage) {
      onLoadMore();
    }
  };

  if (!isLoading && creators.length === 0) {
    return (
      <Box className={layoutStyles.container} data-testid={testID}>
        <Stack direction="row" className={layoutStyles.toolbar}>
          <Box className={layoutStyles.searchContainer}>
            <SearchInput
              value={searchQuery}
              onChange={onSearch}
              onSubmit={onSearch}
              placeholder={translate('Search by creator name...')}
            />
          </Box>
        </Stack>
        <Box className={layoutStyles.emptyState}>
          <Headline size="xs">{translate('No creators yet')}</Headline>
          <Body size="md">
            {translate('Creator stats will appear here once creators join.')}
          </Body>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={layoutStyles.container} data-testid={testID}>
      <Stack direction="row" className={layoutStyles.toolbar}>
        <Box className={layoutStyles.searchContainer}>
          <SearchInput
            value={searchQuery}
            onChange={onSearch}
            onSubmit={onSearch}
            placeholder={translate('Search by creator name...')}
          />
        </Box>
        <Box className={layoutStyles.toolbarActions}>
          <Box className={layoutStyles.resultsCount}>
            {translate('Showing {count}', {count: totalCount})}
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            className={layoutStyles.toolbarSeparator}
          />
        </Box>
      </Stack>
      <Box className={layoutStyles.tableCard}>
        <Table
          testID={`${testID}-table`}
          data={creators}
          columns={columns}
          columnOrder={visibleColumnIds}
          isLoadingInitial={isLoading}
          isLoading={isFetching}
          showProgressBars={isFetching}
          visibleColumns={Object.fromEntries(
            visibleColumnIds.map((id) => [id, true])
          )}
          rowSelection={{}}
          sorting={sorting}
          onSorting={handleSortingChange}
          enableSorting
          manualSorting
          onEndReached={handleEndReached}
          enableVirtualization
          tableScrollTopKey="creatorsStatsTableScrollTop"
          emptyStateComponent={null}
        />
      </Box>
    </Box>
  );
}

export default CreatorsStatsTable;
